import { captureException } from '@sentry/react'
import * as Yup from 'yup'

import type { Values } from '../../AssetForm'
import type { ReactElement } from 'react'
import type { ASSET_STATUS, IAssetFileQuality } from '~/types/assets-management'

import AssetForm from '../../AssetForm'

import LinkButton from '~/components/Buttons/LinkButton'
import Modal from '~/components/Modal'
import { ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { statusOptions } from '~/const/asset'
import { useCreateAssetMutation } from '~/queries/assets'
import { ASSET_PLATFORM } from '~/types/assets-management'
import { getTagsArray } from '~/utils/asset'
import dayjs from '~/utils/dayjs'

import './styles.scss'

const MODAL_ID = 'create-asset-modal'

interface ModalProps {
  isOpen: boolean
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const validationSchema = Yup.object().shape({
  fileType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .required(FORM_ERRORS.REQUIRED)
    .nullable(),
  name: Yup.string().required(FORM_ERRORS.REQUIRED),
  properties: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.object({ label: Yup.string(), value: Yup.string() }).required(FORM_ERRORS.REQUIRED).nullable(),
        value: Yup.string().required(FORM_ERRORS.REQUIRED),
      })
    )
    .required(FORM_ERRORS.REQUIRED),
  status: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .required(FORM_ERRORS.REQUIRED)
    .nullable(),
})

const initialValues: Values = {
  expirationDate: '',
  fileType: null,
  name: '',
  properties: [{ key: null, value: '' }],
  status: statusOptions[0],
}

const CreateAssetModal = ({ isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  const { mutateAsync: createAssetMutation } = useCreateAssetMutation()

  const handleSubmit = async (data: Values, fileQualityIds: IAssetFileQuality[]) => {
    const { expirationDate, fileType, name, properties, status } = data

    try {
      await createAssetMutation({
        expiration_date: dayjs(expirationDate).valueOf(),
        file_quality_ids: fileQualityIds,
        file_type: fileType?.value || '',
        name,
        platform: ASSET_PLATFORM.ANDROID,
        status: status.value as ASSET_STATUS,
        tags: getTagsArray(properties),
      })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  const handleHowToLink = () => {
    window.open('https://www.notion.so/How-to-create-a-distant-asset-f45d51edee6a4641a44fdc01a8ff5baf', '_blank')
  }

  return (
    <Modal className="create-asset-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>
        <div className="create-asset-modal__title">Create a new asset</div>
        <LinkButton backgroundColor="light" label="How to create a distant asset?" onClick={handleHowToLink} />
      </ModalHeader>
      <AssetForm
        disabledFields={['status']}
        initialValues={initialValues}
        modalId={MODAL_ID}
        onCancel={onClose}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      />
    </Modal>
  )
}

export default CreateAssetModal
